// Keyboard controls (ui-flow §11.1): Space = split, W = eject, Q = special, Esc, Enter.
// - One action per keydown: keys are tracked in a keys-down set, so OS auto-repeat never fires
//   again (`KeyboardEvent.repeat` is ignored too).
// - While a text input (input, textarea, contenteditable) is focused, split/eject/special and
//   Enter are not emitted (you can type a nick with spaces and Ws); Esc always is.
// - W emits `ejectHeld` true on keydown and false on keyup, for "Hold W to keep shooting".
// - The cash-out key (money rooms; WELCOME `cashOutKey`, default Q, set with `setCashOutKey`)
//   emits `cashOutHeld` true on keydown and false on keyup (money-mode §4: keydown starts the
//   hold, keyup cancels it). Repeat-safe like every key; not started while typing or with
//   Ctrl/⌘/Alt; always released on keyup, blur and detach. Q still emits `special` as well (the
//   app uses it only while spectating, and `cashOutHeld` only in a money life).
// - Uses `KeyboardEvent.code` (layout-independent: Space, KeyW, KeyQ, Escape, Enter/NumpadEnter).
//
// Details (decided here):
// - With Ctrl, ⌘ or Alt held, only Esc is emitted (the others are browser shortcuts: ⌘W, Ctrl+Q).
// - Enter on a focused button or link is left to that element (it activates it), so one press
//   never does two things.
// - Space's default (page scroll, button activation) is prevented only when the key is not aimed
//   at an interactive element, so menus stay keyboard-operable (ui-flow §15).
// - Window blur releases every key (and W's `ejectHeld`), so a key held while switching tabs does
//   not stick.
//
// Implementer: client input/UI. Consumer: app.ts.

import { Emitter } from '../net/socket.js';

export type KeyAction = 'split' | 'eject' | 'special' | 'escape' | 'enter';

export interface KeyboardEvents {
  action: KeyAction;
  /** W went down (true) or up / window blur (false). */
  ejectHeld: boolean;
  /** The cash-out key went down (true) or up / window blur (false). */
  cashOutHeld: boolean;
}

/** The default cash-out key (money-mode §4, `money.cashOutKey`). */
export const DEFAULT_CASH_OUT_CODE = 'KeyQ';

/**
 * `KeyboardEvent.code` for a cash-out key name from WELCOME: a letter → `Key<L>`, a digit →
 * `Digit<d>`, anything else is taken as a code already (e.g. `KeyQ`, `Backquote`); empty → KeyQ.
 */
export function cashOutKeyCode(key: string | null | undefined): string {
  const k = (key ?? '').trim();
  if (k.length === 0) return DEFAULT_CASH_OUT_CODE;
  if (k.length === 1) {
    const u = k.toUpperCase();
    if (u >= 'A' && u <= 'Z') return `Key${u}`;
    if (u >= '0' && u <= '9') return `Digit${u}`;
    return DEFAULT_CASH_OUT_CODE;
  }
  return k;
}

/** `KeyboardEvent.code` → action (ui-flow §11.1). */
export const KEY_BINDINGS: Readonly<Record<string, KeyAction>> = Object.freeze({
  Space: 'split',
  KeyW: 'eject',
  KeyQ: 'special',
  Escape: 'escape',
  Enter: 'enter',
  NumpadEnter: 'enter',
});

/** `<input type>`s that take typed text. */
const TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
  '',
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
  'date',
  'datetime-local',
  'month',
  'time',
  'week',
]);

/** The DOM bits these checks read (real elements, or fakes in tests). */
interface ElementLike {
  readonly tagName?: string;
  readonly type?: string;
  readonly isContentEditable?: boolean;
  getAttribute?(name: string): string | null;
}

function asElement(v: unknown): ElementLike | null {
  return v !== null && typeof v === 'object' && typeof (v as ElementLike).tagName === 'string'
    ? (v as ElementLike)
    : null;
}

/** True for an element that takes typed text: text-like inputs, textarea, select, contenteditable. */
export function isTextEntry(el: unknown): boolean {
  const e = asElement(el);
  if (!e) return false;
  const tag = (e.tagName ?? '').toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return TEXT_INPUT_TYPES.has((e.type ?? '').toLowerCase());
  return e.isContentEditable === true;
}

/** True for an element that handles Enter/Space itself (buttons, links, form controls). */
export function isInteractive(el: unknown): boolean {
  const e = asElement(el);
  if (!e) return false;
  if (isTextEntry(e)) return true;
  const tag = (e.tagName ?? '').toUpperCase();
  if (tag === 'BUTTON' || tag === 'A' || tag === 'SUMMARY' || tag === 'INPUT') return true;
  const role = e.getAttribute?.('role') ?? null;
  return role === 'button' || role === 'link' || role === 'checkbox' || role === 'switch';
}

export class KeyboardInput {
  readonly events = new Emitter<KeyboardEvents>();
  protected readonly target: Window;
  private readonly down = new Set<string>();
  private ejectHeldOut = false;
  private cashOutCode = DEFAULT_CASH_OUT_CODE;
  private cashOutHeldOut = false;
  private attached = false;
  private readonly onKeyDown = (ev: KeyboardEvent): void => this.keyDown(ev);
  private readonly onKeyUp = (ev: KeyboardEvent): void => this.keyUp(ev);
  private readonly onBlur = (): void => this.releaseAll();

  constructor(target: Window) {
    this.target = target;
  }

  /** Adds keydown/keyup/blur listeners. */
  attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.target.addEventListener('keydown', this.onKeyDown);
    this.target.addEventListener('keyup', this.onKeyUp);
    this.target.addEventListener('blur', this.onBlur);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
    this.releaseAll();
  }

  /** The cash-out key from WELCOME (`money.cashOutKey`, e.g. "Q"). Releases a held one first. */
  setCashOutKey(key: string | null | undefined): void {
    const code = cashOutKeyCode(key);
    if (code === this.cashOutCode) return;
    this.releaseCashOut();
    this.cashOutCode = code;
  }

  /** The `KeyboardEvent.code` of the cash-out key. */
  get cashOutKey(): string {
    return this.cashOutCode;
  }

  /** True while the key with this `code` is held. */
  isDown(code: string): boolean {
    return this.down.has(code);
  }

  // -------------------------------------------------------------------------------------------

  /** The element the key is aimed at: the event target if it is an element, else the focus. */
  private focusOf(ev: Event): unknown {
    const t = asElement(ev.target);
    if (t) return t;
    try {
      return this.target.document?.activeElement ?? null;
    } catch {
      return null;
    }
  }

  private keyDown(ev: KeyboardEvent): void {
    const code = ev.code;
    if (!code) return;
    const action = KEY_BINDINGS[code];
    const focus = this.focusOf(ev);
    const typing = isTextEntry(focus) || isTextEntry(this.activeElement());
    if (action === 'split' && !typing && !isInteractive(focus) && ev.cancelable) {
      ev.preventDefault(); // also on auto-repeats
    }
    if (ev.repeat || this.down.has(code)) return;
    this.down.add(code);
    const modified = ev.ctrlKey || ev.metaKey || ev.altKey;
    if (code === this.cashOutCode && !modified && !typing && !this.cashOutHeldOut) {
      this.cashOutHeldOut = true;
      this.events.emit('cashOutHeld', true);
    }
    if (!action) return;
    if (action === 'escape') {
      this.events.emit('action', 'escape');
      return;
    }
    if (modified || typing) return;
    if (action === 'enter' && isInteractive(focus)) return;
    this.events.emit('action', action);
    if (action === 'eject' && !this.ejectHeldOut) {
      this.ejectHeldOut = true;
      this.events.emit('ejectHeld', true);
    }
  }

  private keyUp(ev: KeyboardEvent): void {
    const code = ev.code;
    if (!code) return;
    this.down.delete(code);
    if (code === 'KeyW' && this.ejectHeldOut) {
      this.ejectHeldOut = false;
      this.events.emit('ejectHeld', false);
    }
    if (code === this.cashOutCode) this.releaseCashOut();
  }

  private releaseAll(): void {
    this.down.clear();
    if (this.ejectHeldOut) {
      this.ejectHeldOut = false;
      this.events.emit('ejectHeld', false);
    }
    this.releaseCashOut();
  }

  private releaseCashOut(): void {
    if (!this.cashOutHeldOut) return;
    this.cashOutHeldOut = false;
    this.events.emit('cashOutHeld', false);
  }

  private activeElement(): unknown {
    try {
      return this.target.document?.activeElement ?? null;
    } catch {
      return null;
    }
  }
}
